/**
 * MenuValidation - Main validation dashboard component
 * 
 * Story 1.7: Updated architecture for modal + iframe z-layering
 * 
 * Displays menu validation results including:
 * - Summary cards showing aggregate validation metrics
 * - Filterable validation table (full width initially)
 * - On-demand item preview panel (appears when user clicks View or row)
 * - Associated items modal with potential iframe overlay
 * - Loading and error states
 * 
 * NEW: Modal and iframe can coexist when preview opened from modal context
 */

import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import queryString from "query-string";
import Loader from "../../components/Loader";
import { toast } from "../../components/Toast";
import ValidationSummary from "./ValidationSummary";
import ValidationTable from "./ValidationTable";
import AssociatedItemsModal from "./AssociatedItemsModal";
import ItemPreviewPanel from "./ItemPreviewPanel";

const MenuValidation = () => {
  const location = useLocation();
  
  const [validationResults, setValidationResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [menuId, setMenuId] = useState(null);
  const [selectedModal, setSelectedModal] = useState(null);

  // Story 1.7: Panel state management with sourceContext tracking
  const [panelState, setPanelState] = useState({
    isOpen: false,
    itemUrl: '',
    itemName: '',
    itemId: '',
    sourceContext: null // 'table' or 'modal'
  });

  // Set page title
  useEffect(() => {
    document.title = "grubcenter-menu-validation";
  }, []);

  useEffect(() => {
    const queryParams = location.search;
    const params = queryString.parse(queryParams);
    
    const loadValidationData = async (menuIdValue) => {
      await fetchValidationResults(menuIdValue);
    };
    
    if (params.menuId) {
      setMenuId(params.menuId);
      loadValidationData(params.menuId);
    } else {
      toast.error("Please add menuId to router params", { autoClose: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const fetchValidationResults = async (menuIdParam) => {
    setIsLoading(true);
    
    try {
      const appApiEndPoint = process.env.REACT_APP_WEB_ORDER_API_URL;
      const url = `${appApiEndPoint}/test/menus/${menuIdParam}/validations`;
      
      const response = await axios.get(url);
      
      setValidationResults(response.data);
    } catch (error) {
      toast.error(`Failed to fetch validation results: ${error.message}`, { autoClose: false });
    } finally {
      setIsLoading(false);
    }
  };

  // Story 1.7: Handle modal close
  const handleCloseModal = () => {
    setSelectedModal(null);
    // Story 1.7: Close iframe preview if it was opened from modal context
    if (panelState.isOpen && panelState.sourceContext === 'modal') {
      handleClosePanel();
    }
  };

  // Story 1.7: Handle opening modal from table
  // NEW: When opening modal, keep panel visible if it was opened from modal context
  const handleOpenModal = (item) => {
    // Close preview panel if it was opened from table context
    if (panelState.isOpen && panelState.sourceContext === 'table') {
      handleClosePanel();
    }
    // Open the modal (panel may still be open if it was from modal context)
    setSelectedModal(item);
  };

  // Story 1.7: Panel handlers with sourceContext tracking
  const handleOpenPanel = (item, sourceContext = 'table') => {
    const locale = validationResults?.baseLanguage ? validationResults.baseLanguage.toLowerCase() : "en-us";
    const url = `/item-translated/${item.categoryId}/${item.id}/0?locale=${encodeURIComponent(locale)}&menuId=${encodeURIComponent(menuId)}&hideBack=true`;
    
    // If opening from table context: close modal if open
    if (sourceContext === 'table' && selectedModal) {
      setSelectedModal(null);
    }
    // If opening from modal context: keep modal open, just open panel
    
    setPanelState({
      isOpen: true,
      itemUrl: url,
      itemName: item.name || 'Item Details',
      itemId: item.id,
      sourceContext
    });
  };

  const handleClosePanel = () => {
    setPanelState({
      isOpen: false,
      itemUrl: '',
      itemName: '',
      itemId: '',
      sourceContext: null
    });
  };

  return (
    <div className="ls-validation">
      {isLoading && <Loader />}
      
      {!isLoading && validationResults && (
        <>
          <div className="ls-validation-header-container">
            <div className="ls-validation-header">
              <h1 className="ls-validation-title">
                {validationResults.menuName || "Menu Validation"}
              </h1>
              <p className="ls-validation-subtitle">Summary of Missing Fields</p>
            </div>
          </div>
          
          <ValidationSummary summary={validationResults.summary} />
          
          {/* Story 1.7: Container for side-by-side layout when panel open from TABLE only */}
          <div className={`ls-validation-content-container ${panelState.isOpen && panelState.sourceContext === 'table' ? 'ls-validation-content-container--panel-open' : ''}`}>
            <ValidationTable 
              validationResults={validationResults.validationResults}
              menuId={menuId}
              baseLanguage={validationResults.baseLanguage}
              onOpenModal={handleOpenModal}
              onOpenPanel={(item) => handleOpenPanel(item, 'table')}
              isPanelOpen={panelState.isOpen && panelState.sourceContext === 'table'}
            />

            {/* Story 1.7: Preview panel from TABLE context (side-by-side, same z-axis) */}
            {panelState.isOpen && panelState.sourceContext === 'table' && (
              <ItemPreviewPanel 
                isOpen={panelState.isOpen}
                itemUrl={panelState.itemUrl}
                itemName={panelState.itemName}
                onClose={handleClosePanel}
              />
            )}
          </div>

          {/* Associated Items Modal with potential iframe overlay */}
          {selectedModal && (
            <AssociatedItemsModal
              validationItem={selectedModal}
              onClose={handleCloseModal}
              baseLanguage={validationResults.baseLanguage}
              menuId={menuId}
              onOpenPanel={(item) => handleOpenPanel(item, 'modal')}
              isPreviewOpen={panelState.isOpen && panelState.sourceContext === 'modal'}
            />
          )}

          {/* Story 1.7: Preview panel from MODAL context (overlay z-layer, right side) */}
          {panelState.isOpen && panelState.sourceContext === 'modal' && (
            <ItemPreviewPanel 
              isOpen={panelState.isOpen}
              itemUrl={panelState.itemUrl}
              itemName={panelState.itemName}
              onClose={handleClosePanel}
              isModalContext={true}
            />
          )}
        </>
      )}
    </div>
  );
};

export default MenuValidation;

